import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { ModalOverlayBoundary } from './overlay-environment';

import { cn } from '@/lib/utils';

import { DRAWER_APPEARANCE, DRAWER_DISMISSAL } from './types/drawer-variants-enums';





interface DrawerFoundationContextValue {
  appearance: DRAWER_APPEARANCE;
  dismissal: DRAWER_DISMISSAL;
  busy: boolean;
}

const DrawerFoundationContext = React.createContext<DrawerFoundationContextValue>({
  appearance: DRAWER_APPEARANCE.FOUNDATION,
  dismissal: DRAWER_DISMISSAL.STANDARD,
  busy: false,
});

function shouldBlockDismissal(dismissal: DRAWER_DISMISSAL, busy: boolean): boolean {
  if (busy) {
    return true;
  }

  return dismissal === DRAWER_DISMISSAL.BLOCKED;
}

type DrawerRootProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  dismissal?: DRAWER_DISMISSAL;
  busy?: boolean;
};

const Drawer = ({
  shouldScaleBackground = true,
  dismissal,
  busy = false,
  dismissible: dismissibleProp,
  onOpenChange,
  ...props
}: DrawerRootProps) => {
  const resolvedDismissal = dismissal ?? DRAWER_DISMISSAL.STANDARD;
  const blockDismissal =
    (dismissal !== undefined || busy) && shouldBlockDismissal(resolvedDismissal, busy);
  const dismissible = dismissibleProp ?? (blockDismissal ? false : undefined);

  const handleOpenChange = (open: boolean) => {
    if (blockDismissal && !open) {
      return;
    }
    onOpenChange?.(open);
  };

  return (
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      {...(dismissible !== undefined ? { dismissible } : {})}
      onOpenChange={handleOpenChange}
      {...props}
    />
  );
};
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const foundationDrawerOverlayClasses =
  'fixed inset-0 z-overlay bg-overlay-scrim motion-reduce:transition-none';

interface DrawerOverlayProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> {
  appearance?: DRAWER_APPEARANCE;
}

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  DrawerOverlayProps
>(({ appearance: _appearance, className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Overlay
      ref={ref}
      data-slot="drawer-overlay"
      className={cn(foundationDrawerOverlayClasses, className)}
      {...props}
    />
  );
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const foundationDrawerContentClasses =
  'fixed inset-x-0 bottom-0 z-portal mt-24 flex h-auto max-h-[100dvh] flex-col overflow-hidden rounded-t-surface border border-border-default bg-surface-raised pb-[env(safe-area-inset-bottom)] text-content-primary shadow-raised outline-none transition-[transform] duration-deliberate ease-standard motion-reduce:transition-none';

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  appearance?: DRAWER_APPEARANCE;
  dismissal?: DRAWER_DISMISSAL;
  busy?: boolean;
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      appearance,
      dismissal,
      busy = false,
      className,
      children,
      onEscapeKeyDown,
      onPointerDownOutside,
      onInteractOutside,
      ...props
    },
    ref
  ) => {
    const resolvedAppearance = appearance ?? DRAWER_APPEARANCE.FOUNDATION;
    const resolvedDismissal = dismissal ?? DRAWER_DISMISSAL.STANDARD;
    const blockDismissal = shouldBlockDismissal(resolvedDismissal, busy);
    const foundationContextValue = React.useMemo(
      () => ({
        appearance: resolvedAppearance,
        dismissal: resolvedDismissal,
        busy,
      }),
      [resolvedAppearance, resolvedDismissal, busy]
    );

    const handleEscapeKeyDown = (
      event: Parameters<NonNullable<typeof onEscapeKeyDown>>[0]
    ) => {
      if (blockDismissal) {
        event.preventDefault();
      }
      onEscapeKeyDown?.(event);
    };

    const handlePointerDownOutside = (
      event: Parameters<NonNullable<typeof onPointerDownOutside>>[0]
    ) => {
      if (blockDismissal) {
        event.preventDefault();
      }
      onPointerDownOutside?.(event);
    };

    const handleInteractOutside = (
      event: Parameters<NonNullable<typeof onInteractOutside>>[0]
    ) => {
      if (blockDismissal) {
        event.preventDefault();
      }
      onInteractOutside?.(event);
    };

    return (
      <DrawerPortal>
        <DrawerOverlay appearance={resolvedAppearance} />
        <DrawerPrimitive.Content
          ref={ref}
          data-slot="drawer-content"
          data-side="bottom"
          data-dismissal={resolvedDismissal}
          data-busy={busy || undefined}
          aria-busy={busy ? true : undefined}
          onEscapeKeyDown={handleEscapeKeyDown}
          onPointerDownOutside={handlePointerDownOutside}
          onInteractOutside={handleInteractOutside}
          className={cn(foundationDrawerContentClasses, className)}
          {...props}
        >
          <DrawerFoundationContext value={foundationContextValue}>
            <ModalOverlayBoundary>
              <div
                data-slot="drawer-handle"
                className={
                  // Muted background exception: inactive-chrome
                  'mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted'
                }
              />
              {children}
            </ModalOverlayBoundary>
          </DrawerFoundationContext>
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  }
);
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      data-slot="drawer-header"
      className={cn('grid gap-1.5 p-4 text-center sm:text-left shrink-0', className)}
      {...props}
    />
  );
};
DrawerHeader.displayName = 'DrawerHeader';

const DrawerBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      data-slot="drawer-body"
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4',
        className
      )}
      {...props}
    />
  );
};
DrawerBody.displayName = 'DrawerBody';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      data-slot="drawer-footer"
      className={cn(
        'mt-auto flex flex-col gap-2 p-4 shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))]',
        className
      )}
      {...props}
    />
  );
};
DrawerFooter.displayName = 'DrawerFooter';

interface DrawerTitleProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> {
  appearance?: DRAWER_APPEARANCE;
}

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  DrawerTitleProps
>(({ appearance: _appearance, className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Title
      ref={ref}
      data-slot="drawer-title"
      className={cn('type-dialog-title font-semibold text-content-primary', className)}
      {...props}
    />
  );
});
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

interface DrawerDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> {
  appearance?: DRAWER_APPEARANCE;
}

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  DrawerDescriptionProps
>(({ appearance: _appearance, className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Description
      ref={ref}
      data-slot="drawer-description"
      className={cn('text-sm text-content-secondary', className)}
      {...props}
    />
  );
});
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export { Drawer, DrawerBody, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerPortal, DrawerTitle, DrawerTrigger };

export type { DrawerContentProps, DrawerOverlayProps, DrawerRootProps };
